<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { voyadaHeroMode } from '@/composables/useVoyadaFlag'
import { useMenuData } from '@/composables/useMenuData'
import { showMobileLeftMenu } from '@/composables/useMobileMenu'

const { menu } = useMenuData()

const destinationMenu = computed(() => menu.value.find((m) => /destination/i.test(m.text)) ?? null)
const tripStylesMenu = computed(() => menu.value.find((m) => /trip.?style/i.test(m.text)) ?? null)
const dealsMenu = computed(() => menu.value.find((m) => /deals/i.test(m.text)) ?? null)

const isVoyada = computed(() => voyadaHeroMode.value === 'voyada')
const accentColor = computed(() => (isVoyada.value ? '#4caf73' : '#333333'))

const openSections = reactive({ destination: true, tripStyles: false, deals: false })
const activeDestL1 = ref(0)
const activeTripL1 = ref(0)

function toggleSection(key: keyof typeof openSections) {
  openSections[key] = !openSections[key]
}

const openNavGroups = reactive<Record<string, boolean>>({})

function toggleNavGroup(title: string) {
  openNavGroups[title] = !openNavGroups[title]
}

const navGroups = [
  {
    title: 'Company',
    links: [
      { text: 'About Us', href: '/about/' },
      { text: 'Large Group Partnership', href: '/registrationByGroup/' },
      { text: 'Partnering with TripToGo', href: '/partner/' },
      { text: 'Travel Inspiration', href: 'https://inspiration.triptogo.com' }
    ]
  },
  {
    title: 'Travel Service Providers',
    links: [
      { text: 'Working with TripToGo', href: '/workwithus/' },
      { text: 'Operator Portal', href: 'https://data.triptogo.com/operator/' },
      { text: 'Operator Registration', href: '/registration/' }
    ]
  },
  {
    title: 'Travel Agents',
    links: [
      { text: 'Agents Log In', href: '/agentlogin/#agencyLogin' },
      { text: 'Agent Registration', href: '/agentlogin/#agencyRegister' },
      { text: 'Agent FAQ', href: '/agentFaq/' },
      { text: 'Travel Agent Support', href: '/contactUs/?agent=1' }
    ]
  },
  {
    title: 'Travel Insurance',
    links: [{ text: 'Travel Protection', href: '/insurance/' }]
  },
  {
    title: 'Policy',
    links: [
      { text: 'General', href: '/terms/?title=general' },
      { text: 'Privacy', href: '/terms/?title=privacy' },
      { text: 'Refund', href: '/terms/?title=refund' },
      { text: 'Cookies', href: '/terms/?title=cookies' }
    ]
  },
  {
    title: 'Membership',
    links: [{ text: 'Join Now', href: '/membership' }]
  },
  {
    title: 'Visa Advisory',
    links: [
      { text: 'Visa Assistance', href: '/visa' },
      { text: 'Visa Application', href: 'https://cibtvisas.com/?login=triptogo' }
    ]
  },
  {
    title: 'Support',
    links: [
      { text: 'Contact Us', href: '/contactUs/' },
      { text: 'FAQ', href: '/FAQ/' },
      { text: '24/7 Live Assistance', href: '/contactUs/' }
    ]
  }
]
</script>

<template>
  <div
    class="overlay overlay--classic"
    :style="{ '--accent': accentColor }"
  >
    <!-- DESTINATION -->
    <div v-if="destinationMenu" class="section">
      <button class="section__header" @click="toggleSection('destination')">
        <ITablerMapPinFilled class="section__icon" />
        <span class="section__title">DESTINATION</span>
        <svg
          class="section__chevron"
          :class="{ rotated: openSections.destination }"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
        >
          <path d="M1 0.75L6 5.25L11 0.75" stroke="currentColor" stroke-width="1.5" />
        </svg>
      </button>
      <div v-if="openSections.destination" class="two-col">
        <div class="two-col__left">
          <a
            v-for="(l1, idx) in destinationMenu.children"
            :key="idx"
            :href="l1.link"
            class="two-col__item"
            :class="{ 'two-col__item--active': activeDestL1 === idx }"
            @click.prevent="activeDestL1 = idx"
            >{{ l1.text }}</a
          >
        </div>
        <div class="two-col__divider" />
        <div
          class="two-col__right"
          v-if="destinationMenu.children?.[activeDestL1]?.children?.length"
        >
          <a
            v-if="destinationMenu.children[activeDestL1].data?.type === 'category'"
            :href="destinationMenu.children[activeDestL1].link"
            class="two-col__item two-col__item--view-all"
          >View All {{ destinationMenu.children[activeDestL1].data.title }} Tours ({{ destinationMenu.children[activeDestL1].data.productCount }})</a>
          <a
            v-for="(l2, idx) in destinationMenu.children[activeDestL1].children"
            :key="idx"
            :href="l2.link"
            class="two-col__item"
            >{{ l2.text }}</a
          >
        </div>
      </div>
    </div>

    <!-- TRIP STYLES -->
    <div v-if="tripStylesMenu" class="section">
      <button class="section__header" @click="toggleSection('tripStyles')">
        <ITablerBinocularsFilled class="section__icon" />
        <span class="section__title">TRIP STYLES</span>
        <svg
          class="section__chevron"
          :class="{ rotated: openSections.tripStyles }"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
        >
          <path d="M1 0.75L6 5.25L11 0.75" stroke="currentColor" stroke-width="1.5" />
        </svg>
      </button>
      <div v-if="openSections.tripStyles" class="two-col">
        <div class="two-col__left">
          <a
            v-for="(l1, idx) in tripStylesMenu.children"
            :key="idx"
            :href="l1.link"
            class="two-col__item"
            :class="{ 'two-col__item--active': activeTripL1 === idx }"
            @click.prevent="activeTripL1 = idx"
            >{{ l1.text }}</a
          >
        </div>
        <div class="two-col__divider" />
        <div
          class="two-col__right"
          v-if="tripStylesMenu.children?.[activeTripL1]?.children?.length"
        >
          <a
            v-if="tripStylesMenu.children[activeTripL1].data?.type === 'category'"
            :href="tripStylesMenu.children[activeTripL1].link"
            class="two-col__item two-col__item--view-all"
          >View All {{ tripStylesMenu.children[activeTripL1].data.title }} Tours ({{ tripStylesMenu.children[activeTripL1].data.productCount }})</a>
          <a
            v-for="(l2, idx) in tripStylesMenu.children[activeTripL1].children"
            :key="idx"
            :href="l2.link"
            class="two-col__item"
            >{{ l2.text }}</a
          >
        </div>
      </div>
    </div>

    <!-- DEALS -->
    <div v-if="dealsMenu" class="section">
      <button class="section__header" @click="toggleSection('deals')">
        <ITablerSpeakerphone class="section__icon" />
        <span class="section__title">DEALS</span>
        <svg
          class="section__chevron"
          :class="{ rotated: openSections.deals }"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
        >
          <path d="M1 0.75L6 5.25L11 0.75" stroke="currentColor" stroke-width="1.5" />
        </svg>
      </button>
      <div v-if="openSections.deals" class="deals-list">
        <a
          v-for="(item, idx) in dealsMenu.children"
          :key="idx"
          :href="item.link"
          class="deals-list__item"
          @click="showMobileLeftMenu = false"
        >{{ item.text }}</a>
      </div>
    </div>

    <!-- NAVIGATION -->
    <div class="section section--nav">
      <div class="section__header section__header--no-chevron">
        <ITablerInfoOctagonFilled class="section__icon" />
        <span class="section__title">NAVIGATION</span>
      </div>
      <div class="nav-groups">
        <!-- Divider line only above the first nav group (between Trip Styles and Company) -->
        <div class="nav-divider" />
        <div v-for="group in navGroups" :key="group.title" class="nav-group">
          <button class="nav-group__header" @click="toggleNavGroup(group.title)">
            <span>{{ group.title }}</span>
            <svg
              :class="{ rotated: openNavGroups[group.title] }"
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
            >
              <path
                d="M1 1l6 5-6 5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <div v-if="openNavGroups[group.title]" class="nav-group__links">
            <a
              v-for="link in group.links"
              :key="link.text"
              :href="link.href"
              target="_blank"
              rel="noopener"
              class="nav-group__link"
              @click="showMobileLeftMenu = false"
              >{{ link.text }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.overlay {
  width: 100%;
  display: flex;
  flex-direction: column;

  &--voyada {
    color: #fff;

    .section {
      border-bottom-color: rgba(255, 255, 255, 0.15);
    }

    .two-col__divider {
      background: rgba(255, 255, 255, 0.2);
    }

    .two-col__item {
      color: #fff;
      &:hover {
        background: rgba(255, 255, 255, 0.08);
      }
    }

    .deals-list__item {
      color: #fff;
      &:hover {
        background: rgba(255, 255, 255, 0.08);
      }
    }

    .nav-divider {
      background: #ccc;
    }

    .nav-group__header {
      color: #fff;
      border-bottom-color: rgba(255, 255, 255, 0.08);
    }

    .nav-group__link {
      color: rgba(255, 255, 255, 0.7);
      &:hover {
        color: #fff;
      }
    }
  }

  &--classic {
    color: #333333;

    .section {
      border-bottom-color: rgba(0, 0, 0, 0.1);
    }

    .two-col__divider {
      background: rgba(0, 0, 0, 0.15);
    }

    .two-col__item {
      color: #333333;
      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }

    .deals-list__item {
      color: #333333;
      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }

    .nav-divider {
      background: #333333;
    }

    .nav-group__header {
      color: #333333;
      border-bottom-color: rgba(0, 0, 0, 0.08);
    }

    .nav-group__link {
      color: rgba(0, 0, 0, 0.6);
      &:hover {
        color: #333333;
      }
    }
  }
}

.section {
  border-bottom: 1px solid;

  &--nav {
    border-bottom: none;
  }

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 0;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    color: inherit;
    text-align: left;

    &--no-chevron {
      /* no extra styles needed — chevron simply not rendered */
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &__title {
    font-family: Poppins, sans-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    flex: 1;
  }

  &__chevron {
    transition: transform 0.2s ease;
    opacity: 0.7;

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

.two-col {
  display: flex;
  flex-direction: row;
  padding-bottom: 16px;

  &__left,
  &__right {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
  }

  &__divider {
    width: 1px;
    background: rgba(255, 255, 255, 0.2);
    margin: 0 12px;
    align-self: stretch;
  }

  &__item {
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: #fff;
    display: flex;
    padding: 0 16px;
    gap: 8px;
    align-self: stretch;
    border-left: 3px solid transparent;
    transition:
      background 0.15s,
      border-color 0.15s;
    min-height: 28px;
    align-items: center;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    &--active {
      border-left-color: var(--accent);
      font-weight: 600;
    }

    &--view-all {
      font-weight: 600;
      color: var(--accent);
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      padding-bottom: 8px;
      margin-bottom: 0;
    }
  }
}

.overlay--classic .two-col__item--view-all {
  color: var(--accent);
  border-bottom-color: rgba(0, 0, 0, 0.1);
}

.deals-list {
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
  gap: 16px;

  &__item {
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    display: flex;
    padding: 0 16px;
    align-self: stretch;
    border-left: 3px solid transparent;
    transition:
      background 0.15s,
      border-color 0.15s;
    min-height: 28px;
    align-items: center;
  }
}

.nav-divider {
  height: 1px;
  background: #ccc;
  margin: 4px 0 8px;
}

.nav-groups {
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
}

.nav-group {
  &__header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    background: none;
    border: none;
    color: inherit;
    text-align: left;

    svg {
      transition: transform 0.2s;
      opacity: 0.6;

      &.rotated {
        transform: rotate(90deg);
      }
    }
  }

  &__links {
    display: flex;
    flex-direction: column;
    padding: 4px 0 8px 12px;
    gap: 2px;
  }

  &__link {
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    padding: 5px 0;

    &:hover {
      color: #fff;
    }
  }
}
</style>
